import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { config } from "../../../../Environment";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import useProcess from "../../../../hooks/useProcess";
import { Edit } from "lucide-react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../../../components/ui/pagination";
import LoggerContext from "../../../../context/LoggerProvider";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../../../../hooks/useAuth";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const itemsPerPage = 4;

const UnfinishedEngagementModal = ({
  unfinishedEngagementModal,
  setUnfinishedEngagementModal,
  prescription,
  setPrescriptions,
  refreshLists,
}: any) => {
  const { auth } = useAuth();

  const [editingEngagement, setEditingEngagement] = useState<
    | {
        id: string;
        name: string;
      }
    | any
  >({
    id: "",
    name: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = useMemo(() => {
    const runningProcesses =
      prescription?.processes.filter((p: any) => p.status === "RUNNING") || [];
    return Math.ceil(runningProcesses.length / itemsPerPage);
  }, [prescription?.processes, itemsPerPage]);

  const { Logger } = useContext(LoggerContext);

  const retryFetch = useRetryFetch();
  const { startProcess, resumeProcess } = useProcess();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const currentEngagements = useMemo(() => {
    const runningProcesses =
      prescription?.processes.filter((p: any) => p.status === "RUNNING") || [];
    const startIndex = (currentPage - 1) * itemsPerPage;
    return runningProcesses.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, prescription?.processes, itemsPerPage]);

  const handleUpdateProcessName = async (
    processId: string,
    newName: string
  ) => {
    if (newName.trim() === "") return;

    try {
      const updateResponse = await retryFetch(
        `${config.API_BASE}/api/process/${processId}/engagement-name`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            engagementName: newName,
          }),
        }
      );

      const data = await updateResponse.json();

      if (!updateResponse.ok) {
        throw new Error(data?.error || "Failed to update process name");
      }

      setPrescriptions((prevPrescriptions: any) =>
        prevPrescriptions.map((prescription: any) => {
          if (prescription._id === data.process.fiPrescription) {
            return {
              ...prescription,
              processes: prescription.processes.map((process: any) =>
                process._id === processId
                  ? { ...process, engagementName: newName }
                  : process
              ),
            };
          }
          return prescription;
        })
      );
    } catch (error) {
      console.error("Failed to update process name:", error);
    } finally {
      setEditingEngagement(null);
    }
  };

  const handleStartNew = async () => {
      const prescriptionId = prescription._id;
      await startProcess({
        prescriptionId,
        // from: location,
        // forward: location,
        onError: () => {
          toast.error("An error occurred while starting the prescription.");
        },
      });
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const range: (number | "...")[] = [];
    const delta = 1;

    const left = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    range.push(1);

    if (left > 2) range.push("...");

    for (let i = left; i <= right; i++) {
      range.push(i);
    }

    if (right < totalPages - 1) range.push("...");

    if (totalPages > 1) range.push(totalPages);

    return range;
  };

  const pageNumbers = useMemo(getPageNumbers, [currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [unfinishedEngagementModal]);

  const continueProcess = async (process: any) => {
      let processToContinue = process._id;
      if (process.pendingInnerProcess) {
        Logger.debug("PENDING INNER PROCESS detected, switching to this one!");
        processToContinue = processToContinue.pendingInnerProcess;
      }
      await resumeProcess({
        processId: processToContinue,
        onError: () => {
          toast.error("An error occurred while continuing the process. Please try again.");
          refreshLists();
        },
      });
  };

  return (
    <Dialog
      open={unfinishedEngagementModal}
      onOpenChange={setUnfinishedEngagementModal}
    >
      <DialogContent className="max-w-2xl h-fit max-h-[90vh] md:h-[34rem] gap-y-2 px-2 md:px-6">
        <DialogHeader>
          <DialogTitle>
            {t("unfinished_engagement_history_dialog_title")}
          </DialogTitle>
          <DialogDescription className="line-clamp-1">
            {t("unfinished_engagement_history_dialog_description", {
              name: prescription?.name,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto pr-2">
          <div className="max-w-2xl">
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                {/* Header with sticky positioning */}
                <TableHeader className="sticky top-0 z-10 bg-background">
                  <TableRow>
                    <TableHead className="min-w-[200px] w-full">
                      {t("engagement_name")}
                    </TableHead>
                    <TableHead className="w-auto text-right p-1 hidden md:table-cell">
                      {t("actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>

                {/* Body with vertical scroll */}
                <TableBody className="overflow-y-auto">
                  {currentEngagements?.map((process: any) => (
                    <TableRow key={process._id} className="h-11 max-h-11">
                      {/* Engagement Name + Edit */}
                      <TableCell className="border-none px-2 py-0 w-full align-middle">
                        {editingEngagement?.id === process._id ? (
                          <div className="flex items-center gap-2 h-8">
                            <Input
                              value={editingEngagement.name}
                              onChange={(e) =>
                                setEditingEngagement({
                                  ...editingEngagement,
                                  name: e.target.value,
                                })
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleUpdateProcessName(
                                    process._id,
                                    editingEngagement.name
                                  );
                                } else if (e.key === "Escape") {
                                  setEditingEngagement(null);
                                }
                              }}
                              className="h-8 text-sm w-full"
                              autoFocus
                            />
                            <Button
                              size="sm"
                              className="h-8 px-2 py-0"
                              onClick={() =>
                                handleUpdateProcessName(
                                  process._id,
                                  editingEngagement.name
                                )
                              }
                            >
                              {t("save")}
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 h-8">
                            <div className="truncate whitespace-nowrap overflow-hidden flex-1 text-sm">
                              {process.engagementName}
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 flex-shrink-0"
                              onClick={() =>
                                setEditingEngagement({
                                  id: process._id,
                                  name: process.engagementName,
                                })
                              }
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>

                      {/* Resume Button - Own Column */}
                      <TableCell className="border-none px-2 py-0 w-[1%] whitespace-nowrap align-middle">
                        <Button
                          size="sm"
                          className="h-7 text-xs px-3"
                          onClick={() => continueProcess(process)}
                        >
                          {t("resume")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                {/* Footer with pagination - sticky at bottom */}
                {prescription?.processes?.length > 0 && (
                  <TableFooter className="sticky bottom-0 z-10 bg-background border-t">
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-right px-0 py-2 border-none"
                      >
                        <Pagination>
                          <PaginationContent className="justify-center space-x-1">
                            {/* Previous */}
                            <PaginationItem>
                              <PaginationPrevious
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePageChange(currentPage - 1);
                                }}
                                aria-disabled={currentPage === 1}
                              />
                            </PaginationItem>

                            {/* Page Numbers (Only on md and above) */}
                            <div className="hidden md:flex">
                              {pageNumbers.map((page, idx) =>
                                page === "..." ? (
                                  <PaginationItem key={`ellipsis-${idx}`}>
                                    <PaginationEllipsis />
                                  </PaginationItem>
                                ) : (
                                  <PaginationItem key={page}>
                                    <PaginationLink
                                      href="#"
                                      isActive={currentPage === page}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handlePageChange(page);
                                      }}
                                    >
                                      {page}
                                    </PaginationLink>
                                  </PaginationItem>
                                )
                              )}
                            </div>

                            {/* Current Page (visible on mobile) */}
                            {/* <div className="block md:hidden">
                              <PaginationItem>
                                <span className="px-3 py-1 text-sm text-muted-foreground">
                                  Page {currentPage} of {totalPages}
                                </span>
                              </PaginationItem>
                            </div> */}

                            {/* Next */}
                            <PaginationItem>
                              <PaginationNext
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePageChange(currentPage + 1);
                                }}
                                aria-disabled={currentPage === totalPages}
                              />
                            </PaginationItem>
                          </PaginationContent>
                        </Pagination>
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </div>

            {prescription?.processes?.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">
                  {t("engagementHistoryDialogEmptyMessage")}
                </p>
              </div>
            )}
          </div>

          {/* Trendy OR Divider */}
          <div className="flex items-center my-4 md:my-8">
            <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
            <div className="px-4">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-800 rounded-full shadow-lg">
                <span className="text-white font-bold text-sm select-none">
                  {t("or")}
                </span>
              </div>
            </div>
            <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-primary/20 dark:border-primary rounded-lg p-6">
            <div className="flex items-start md:items-center justify-between flex-col md:flex-row">
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-12 h-12 bg-primary rounded-full shadow-lg">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                    {t("new_engagement")}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t("start_from_scratch")}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => handleStartNew()}
                size="lg"
                className=" text-white shadow-lg hover:shadow-xl transition-all duration-200 mt-4 md:mt-0 h-9 md:h-10 px-4"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                {t("start_new")}
              </Button>
            </div>
          </div>

          <DialogFooter className="flex gap-2 h-fit mt-3">
            <Button
              variant="outline"
              onClick={() => setUnfinishedEngagementModal(false)}
            >
              {t("cancel")}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UnfinishedEngagementModal;
